import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiMagnify } from '@mdi/js'
import { Input } from '../ui/input'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import { Table, TableBody, TableHead, TableHeader, TableRow } from '../ui/table'
import {
    useAdminSettingsRegistry,
    type AdminSettingListEntry,
    type RegistryKindFilter,
} from '../../hooks/use-admin-settings'
import { SettingsRegistryRow } from './SettingsRegistryRow'
import { SettingsRegistryDrilldown } from './SettingsRegistryDrilldown'
import { EmptyState } from '../ui/empty-state'
import { mdiCogOutline } from '@mdi/js'

const KIND_TABS: RegistryKindFilter[] = ['all', 'flag', 'setting']

type Notice = { kind: 'success' | 'error'; text: string } | null

export function SettingsRegistryTable() {
    const { t } = useTranslation('admin')
    const [kind, setKind] = useState<RegistryKindFilter>('all')
    const [search, setSearch] = useState('')
    const [drilldown, setDrilldown] = useState<{ code: string; kind: 'flag' | 'setting' } | null>(null)
    const [notice, setNotice] = useState<Notice>(null)

    const { data, isLoading, isError } = useAdminSettingsRegistry(kind)
    const entries = useMemo<AdminSettingListEntry[]>(() => (data as { data?: AdminSettingListEntry[] })?.data ?? [], [data])

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase()
        if (!q) return entries
        return entries.filter((e) => {
            const name = Object.values(e.name ?? {}).join(' ').toLowerCase()
            return e.code.toLowerCase().includes(q) || name.includes(q)
        })
    }, [entries, search])

    return (
        <div className="space-y-4" data-testid="settings-registry">
            <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex gap-4 items-center flex-wrap">
                    <div className="flex items-center border border-border rounded-md px-3 py-2 bg-background min-w-64">
                        <AppIcon tone="muted" path={mdiMagnify} size="sm" className="mr-2" />
                        <Input
                            type="text"
                            placeholder={t('registry.search_placeholder')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="bg-transparent border-none outline-none text-sm flex-1 text-foreground placeholder:text-muted-foreground p-0 h-auto shadow-none focus-visible:ring-0"
                            data-testid="settings-registry-search"
                        />
                    </div>

                    <div className="inline-flex rounded-md border p-1" role="tablist" data-testid="settings-registry-kind-tabs">
                        {KIND_TABS.map((k) => (
                            <button
                                key={k}
                                type="button"
                                onClick={() => setKind(k)}
                                className={`px-3 py-1 text-sm rounded ${kind === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                                data-testid={`settings-registry-kind-${k}`}
                            >
                                {t(`registry.tab.${k}`)}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {notice && (
                <Alert
                    variant={notice.kind === 'error' ? 'destructive' : 'default'}
                    data-testid="settings-registry-notice"
                >
                    <AlertTitle>
                        {notice.kind === 'error' ? t('registry.notice.error_title') : t('registry.notice.success_title')}
                    </AlertTitle>
                    <AlertDescription>{notice.text}</AlertDescription>
                </Alert>
            )}

            {isError && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 text-destructive text-sm" data-testid="settings-registry-error">
                    {t('registry.error')}
                </div>
            )}

            <div className="bg-card rounded-lg border border-border overflow-hidden overflow-x-auto">
                {isLoading ? (
                    <div className="px-6 py-4 text-center text-muted-foreground" data-testid="settings-registry-loading">
                        {t('registry.loading')}
                    </div>
                ) : filtered.length === 0 ? (
                    <EmptyState
                        variant="inline"
                        live
                        icon={mdiCogOutline}
                        title={t('registry.empty')}
                        data-testid="settings-registry-empty"
                    />
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('registry.column.code')}</TableHead>
                                <TableHead>{t('registry.column.kind')}</TableHead>
                                <TableHead>{t('registry.column.stage')}</TableHead>
                                <TableHead>{t('registry.column.default_value')}</TableHead>
                                <TableHead>{t('registry.column.server_value')}</TableHead>
                                <TableHead className="text-right">{t('registry.column.org_overrides')}</TableHead>
                                <TableHead>{t('registry.column.last_read')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.map((entry) => (
                                <SettingsRegistryRow
                                    key={entry.code}
                                    entry={entry}
                                    onViewOverrides={() => setDrilldown({ code: entry.code, kind: entry.kind })}
                                    onNotice={setNotice}
                                />
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>

            <SettingsRegistryDrilldown
                code={drilldown?.code ?? null}
                kind={drilldown?.kind}
                onClose={() => setDrilldown(null)}
            />
        </div>
    )
}
